"use client";

import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { PageHeaderV2, type PageHeaderV2Config } from "components/ui/v2/page-header-v2";
import { FormStepNav } from "./form-step-nav";
import { PageShell } from "./page-shell";
import { PageRoot } from "./page-root";

export interface FormPageShellProps {
  header?: PageHeaderV2Config;
  steps?: { value: string; label: string }[];
  activeStep?: string;
  onStepChange?: (value: string) => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  particleIcon?: LucideIcon;
  /** Form wizard routes use a narrower reading column (max-w-3xl). */
  contentWidth?: "default" | "narrow";
  /** Content sits in the header visual zone — no gap, transparent stage. */
  headerBlend?: boolean;
}

export function FormPageShell({
  header,
  steps,
  activeStep,
  onStepChange,
  children,
  footer,
  particleIcon,
  contentWidth = "default",
  headerBlend = false,
}: FormPageShellProps) {
  const resolvedHeader = header
    ? {
        ...header,
        particleIcon: particleIcon ?? header.particleIcon,
        ...(headerBlend ? { compact: true } : {}),
      }
    : undefined;

  const inner = (
    <>
      {steps && steps.length > 0 ? (
        <FormStepNav
          steps={steps}
          activeStep={activeStep}
          onStepChange={onStepChange}
        />
      ) : null}
      {children}
      {footer ? <footer className="pb-8 pt-2">{footer}</footer> : null}
    </>
  );

  const content =
    contentWidth === "narrow" ? (
      <div className="mx-auto w-full max-w-3xl">{inner}</div>
    ) : (
      inner
    );

  if (headerBlend) {
    const headerNode = resolvedHeader ? (
      <PageHeaderV2 {...resolvedHeader} />
    ) : null;

    return (
      <PageRoot>
        <div className="relative mx-auto min-h-screen max-w-7xl px-3 pb-6 text-slate-900 dark:text-zinc-50 md:px-6">
          {headerNode}
          <div className="relative z-[1] -mt-10">{content}</div>
        </div>
      </PageRoot>
    );
  }

  return (
    <PageShell header={resolvedHeader}>
      {content}
    </PageShell>
  );
}
